import type { CompetencyMatrixGroup, CompetencyMatrixReport } from '@/types/reports';

export interface MatrixHighlights {
    weakestGroup: CompetencyMatrixGroup | null;
    strongestGroup: CompetencyMatrixGroup | null;
}

type RatedGroup = CompetencyMatrixGroup & { average: number };

const isRatedGroup = (group: CompetencyMatrixGroup): group is RatedGroup => group.average !== null;

/** A candidate replaces the current pick on a strictly better average, or — tied — on an earlier
 * `sortOrder`, i.e. the group the matrix itself lists first (Lot C spec: "ex aequo = premier
 * sortOrder"). */
const isEarlierTiebreak = (candidate: RatedGroup, current: RatedGroup): boolean =>
    candidate.average === current.average && candidate.sortOrder < current.sortOrder;

/**
 * Picks the two groups the highlight cards need: the lowest average ("priority to improve") and
 * the highest ("strongest"). Groups without a recorded average (nobody assessed) are ignored —
 * they cannot meaningfully be the weakest or the strongest of anything.
 */
export const selectMatrixHighlights = (
    report: Pick<CompetencyMatrixReport, 'groups'> | undefined,
): MatrixHighlights => {
    const ratedGroups = (report?.groups ?? []).filter(isRatedGroup);

    if (ratedGroups.length === 0) {
        return { weakestGroup: null, strongestGroup: null };
    }

    const weakestGroup = ratedGroups.reduce((weakest, candidate) =>
        candidate.average < weakest.average || isEarlierTiebreak(candidate, weakest)
            ? candidate
            : weakest,
    );

    const strongestGroup = ratedGroups.reduce((strongest, candidate) =>
        candidate.average > strongest.average || isEarlierTiebreak(candidate, strongest)
            ? candidate
            : strongest,
    );

    return { weakestGroup, strongestGroup };
};
